// components/admin/ModerationDashboard.jsx - Admin content moderation dashboard
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    ShieldCheckIcon,
    ShieldExclamationIcon,
    ExclamationTriangleIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    PlusIcon,
    EyeIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

function ModerationDashboard() {
    const [activeTab, setActiveTab] = useState('overview');
    const [newKeywords, setNewKeywords] = useState('');
    const [newDomains, setNewDomains] = useState('');
    const [selectedContent, setSelectedContent] = useState(null);
    const queryClient = useQueryClient();

    // Fetch global moderation statistics
    const { data: globalStats, isLoading: statsLoading } = useQuery(
        'moderationGlobalStats',
        () => fetch('/api/moderation/admin/global-stats', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.json())
    );

    // Fetch review queue
    const { data: reviewQueue, isLoading: queueLoading } = useQuery(
        'moderationReviewQueue',
        () => fetch('/api/moderation/admin/review-queue', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.json())
    );

    // Add keywords mutation
    const addKeywordsMutation = useMutation(
        (keywords) => fetch('/api/moderation/admin/add-keywords', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ keywords })
        }).then(res => res.json()),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('moderationGlobalStats');
                setNewKeywords('');
            }
        }
    );

    // Add domains mutation
    const addDomainsMutation = useMutation(
        (domains) => fetch('/api/moderation/admin/add-domains', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ domains })
        }).then(res => res.json()),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('moderationGlobalStats');
                setNewDomains('');
            }
        }
    );

    // Bulk recheck mutation
    const bulkRecheckMutation = useMutation(
        () => fetch('/api/moderation/admin/bulk-recheck', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.json()),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['moderationGlobalStats', 'moderationReviewQueue']);
            }
        }
    );

    // Manual review mutation
    const manualReviewMutation = useMutation(
        ({ contentId, action, reason }) => fetch('/api/moderation/manual-review', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ contentId, action, reason })
        }).then(res => res.json()),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('moderationReviewQueue');
                setSelectedContent(null);
            }
        }
    );

    const handleAddKeywords = () => {
        if (!newKeywords.trim()) return;

        const keywords = newKeywords
            .split('\n')
            .map(k => k.trim())
            .filter(k => k.length > 0);

        addKeywordsMutation.mutate(keywords);
    };

    const handleAddDomains = () => {
        if (!newDomains.trim()) return;

        const domains = newDomains
            .split('\n')
            .map(d => d.trim())
            .filter(d => d.length > 0);

        addDomainsMutation.mutate(domains);
    };

    const stats = globalStats?.data || {};
    const queue = reviewQueue?.data || [];

    const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
        <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <Icon className={`h-6 w-6 ${color}`} />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                        <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
                            <dd className="text-lg font-medium text-gray-900">{value}</dd>
                            {subtext && <dd className="text-sm text-gray-500">{subtext}</dd>}
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="md:flex md:items-center md:justify-between mb-8">
                <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Content Moderation Dashboard
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Monitor and manage content filtering across the platform
                    </p>
                </div>
                <div className="mt-4 flex md:mt-0 md:ml-4">
                    <button
                        onClick={() => bulkRecheckMutation.mutate()}
                        disabled={bulkRecheckMutation.isLoading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`h-4 w-4 mr-2 ${bulkRecheckMutation.isLoading ? 'animate-spin' : ''}`} />
                        Bulk Recheck
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    {[
                        { id: 'overview', name: 'Overview', icon: ShieldCheckIcon },
                        { id: 'review', name: 'Review Queue', icon: ClockIcon },
                        { id: 'settings', name: 'Settings', icon: ShieldExclamationIcon }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                        >
                            <tab.icon className="h-4 w-4 mr-2" />
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Statistics Grid */}
                    {statsLoading ? (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="bg-gray-200 animate-pulse h-24 rounded-lg"></div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            <StatCard
                                title="Total Content"
                                value={stats.global?.total || 0}
                                icon={ShieldCheckIcon}
                                color="text-blue-600"
                            />
                            <StatCard
                                title="Blocked Content"
                                value={stats.global?.blocked || 0}
                                icon={XCircleIcon}
                                color="text-red-600"
                                subtext={`${((stats.global?.blocked / stats.global?.total) * 100 || 0).toFixed(1)}% of total`}
                            />
                            <StatCard
                                title="Needs Review"
                                value={stats.global?.needsReview || 0}
                                icon={ExclamationTriangleIcon}
                                color="text-yellow-600"
                            />
                            <StatCard
                                title="Active Filters"
                                value={stats.system?.totalKeywords || 0}
                                icon={ShieldExclamationIcon}
                                color="text-green-600"
                                subtext={`${stats.system?.totalDomains || 0} blocked domains`}
                            />
                        </div>
                    )}

                    {/* Moderation Reasons Breakdown */}
                    {stats.global?.moderationReasons && (
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Blocked Content by Reason</h3>
                            <div className="space-y-3">
                                {Object.entries(stats.global.moderationReasons).map(([reason, count]) => (
                                    <div key={reason} className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 capitalize">
                                            {reason.replace(/_/g, ' ').toLowerCase()}
                                        </span>
                                        <span className="text-sm font-medium text-gray-900">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Review Queue Tab */}
            {activeTab === 'review' && (
                <div className="space-y-6">
                    <div className="bg-white shadow rounded-lg">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Content Requiring Review</h3>
                            <p className="text-sm text-gray-500">Content flagged for manual review</p>
                        </div>

                        {queueLoading ? (
                            <div className="p-6">
                                <div className="animate-pulse space-y-4">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="h-16 bg-gray-200 rounded"></div>
                                    ))}
                                </div>
                            </div>
                        ) : queue.length === 0 ? (
                            <div className="p-6 text-center">
                                <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No content needs review</h3>
                                <p className="mt-1 text-sm text-gray-500">All content has been processed</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {queue.map((content) => (
                                    <div key={content._id} className="p-6 flex justify-between items-start">
                                        <div className="flex-1">
                                            <h4 className="text-sm font-medium text-gray-900">{content.title}</h4>
                                            <p className="text-sm text-gray-500 mt-1">{content.sourceUrl}</p>
                                            <div className="mt-2 flex items-center space-x-4">
                                                <span className="text-xs text-gray-500">
                                                    User: {content.userId?.email}
                                                </span>
                                                {content.metadata?.moderationScore && (
                                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                                        content.metadata.moderationScore >= 7
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        Risk Score: {content.metadata.moderationScore}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="ml-4 flex space-x-2">
                                            <button
                                                onClick={() => setSelectedContent(content)}
                                                className="text-blue-600 hover:text-blue-900 text-sm"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => manualReviewMutation.mutate({
                                                    contentId: content._id,
                                                    action: 'approve'
                                                })}
                                                className="text-green-600 hover:text-green-900 text-sm"
                                                disabled={manualReviewMutation.isLoading}
                                            >
                                                <CheckCircleIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => manualReviewMutation.mutate({
                                                    contentId: content._id,
                                                    action: 'block',
                                                    reason: 'Manual review'
                                                })}
                                                className="text-red-600 hover:text-red-900 text-sm"
                                                disabled={manualReviewMutation.isLoading}
                                            >
                                                <XCircleIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div className="space-y-6">
                    {/* Add Keywords */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Add Custom Keywords</h3>
                        <div className="space-y-4">
                            <textarea
                                value={newKeywords}
                                onChange={(e) => setNewKeywords(e.target.value)}
                                placeholder="Enter keywords (one per line)"
                                className="w-full h-32 border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                            <button
                                onClick={handleAddKeywords}
                                disabled={addKeywordsMutation.isLoading || !newKeywords.trim()}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Add Keywords
                            </button>
                        </div>
                    </div>

                    {/* Add Domains */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Add Custom Domains</h3>
                        <div className="space-y-4">
                            <textarea
                                value={newDomains}
                                onChange={(e) => setNewDomains(e.target.value)}
                                placeholder="Enter domains (one per line, e.g., example.com)"
                                className="w-full h-32 border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                            <button
                                onClick={handleAddDomains}
                                disabled={addDomainsMutation.isLoading || !newDomains.trim()}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Add Domains
                            </button>
                        </div>
                    </div>

                    {/* Current System Stats */}
                    {stats.system && (
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Current Filter Configuration</h3>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Total Keywords</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{stats.system.totalKeywords}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Blocked Domains</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{stats.system.totalDomains}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        {new Date(stats.system.lastUpdated).toLocaleString()}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    )}
                </div>
            )}

            {/* Content Preview Modal */}
            {selectedContent && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Content Review</h3>
                                <button
                                    onClick={() => setSelectedContent(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Title</label>
                                    <p className="text-sm text-gray-900">{selectedContent.title}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Source URL</label>
                                    <p className="text-sm text-gray-900 break-all">{selectedContent.sourceUrl}</p>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Content Preview</label>
                                    <p className="text-sm text-gray-900 max-h-40 overflow-y-auto">
                                        {selectedContent.content?.substring(0, 500)}...
                                    </p>
                                </div>

                                {selectedContent.metadata?.moderationReasons && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Flagged Reasons</label>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            {selectedContent.metadata.moderationReasons.map(reason => (
                                                <span key={reason} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                                    {reason.replace(/_/g, ' ').toLowerCase()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    onClick={() => manualReviewMutation.mutate({
                                        contentId: selectedContent._id,
                                        action: 'approve'
                                    })}
                                    disabled={manualReviewMutation.isLoading}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={() => manualReviewMutation.mutate({
                                        contentId: selectedContent._id,
                                        action: 'block',
                                        reason: 'Manual review decision'
                                    })}
                                    disabled={manualReviewMutation.isLoading}
                                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                                >
                                    Block
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ModerationDashboard;